import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import GiftForSomeoneElse from "assets/images/bustle/vouchers/gift_for_someone_else.png";
import GiftForUnknownRecipient from "assets/images/bustle/vouchers/gift_for_unknown_recipient.png";
import SelfPurchase from "assets/images/bustle/vouchers/self_purchase.png";
import { DateTime } from "luxon";
import { AnimatePresence, motion, Variants } from "motion/react";
import { useForm, UseFormReturn } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { MdDelete } from "react-icons/md";
import {
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom-v5-compat";
import { z } from "zod";

import {
  FormattedCurrencyField,
  HelperText,
  Label,
  TextAreaField,
  TextField,
} from "@onsite_app/MobileOrderingMenu/Atoms/Form";

import { Button, Price } from "@online_ordering/Atoms";
import { PrimaryAction } from "@online_ordering/Atoms";
import NavBarTitle from "@online_ordering/Atoms/Navbar";

import { useVenue } from "@online_ordering/VenueContext";

import VenueOnlineConfigQueries from "../VenueOnlineConfig/queries";
import { VoucherCardPreview } from "./VoucherCartItems/VoucherCardPreview";
import {
  useDeleteVoucherCartItem,
  useSaveVoucherCartItem,
} from "./VoucherCartItems/mutations";
import VoucherCartItemsQueries from "./VoucherCartItems/queries";
import VoucherTemplatesQueries from "./VoucherTemplates/queries";
import { VoucherCartQueries } from "./queries";

const collapseY: Variants = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto", transition: { duration: 0.5 } },
  exit: {
    opacity: 0,
    height: 0,
    transition: { duration: 0.5 },
    marginTop: 0,
  },
};

const BaseFormSchema = z.object({
  voucherTemplateUuid: z.string().nullish(),
  value: z.coerce.number().optional(),
});
const MeForm = BaseFormSchema.extend({
  creationReason: z.literal("self_purchase"),
});
const SomeoneElseForm = BaseFormSchema.extend({
  creationReason: z.literal("gift_for_someone_else"),
  recipientName: z.string().min(1, "Please enter the recipient's name"),
  recipientEmail: z.string().email("Please enter a valid email address"),
  senderName: z.string(),
  message: z.string().optional(),
});
const UnknownRecipientForm = BaseFormSchema.extend({
  creationReason: z.literal("gift_for_unknown_recipient"),
  message: z.string().nullish(),
  senderName: z.string().min(1, "Please enter your name"),
});

const getFormDataSchema = ({
  maxValue,
  minValue,
}: {
  maxValue: number;
  minValue: number;
}) => {
  return z
    .discriminatedUnion("creationReason", [
      UnknownRecipientForm,
      SomeoneElseForm,
      MeForm,
    ])
    .refine(
      (data) =>
        data.voucherTemplateUuid ||
        (!data.voucherTemplateUuid &&
          data.value &&
          data.value <= maxValue &&
          data.value >= minValue),
      {
        message: `Please enter a value between ${minValue} and ${maxValue}`,
        path: ["value"],
      },
    )
    .transform((data) => ({
      ...data,
      valueInCents: Math.round((data.value || 0) * 100),
    }));
};

type FormData = z.infer<ReturnType<typeof getFormDataSchema>>;
const VoucherRadioOption = ({
  value,
  formProps,
  image,
  title,
  description,
}: {
  formProps: UseFormReturn<FormData>;
  image: React.ReactNode;
  title: React.ReactNode;
  description: React.ReactNode;
  value: FormData["creationReason"];
}) => {
  return (
    <div className="flex flex-1">
      <input
        type="radio"
        className="peer sr-only"
        value={value}
        id={value}
        {...formProps.register("creationReason")}
      />
      <label
        htmlFor={value}
        className={
          "block h-full w-full rounded-venue-brand bg-venue-tertiary px-16 py-32 text-center text-venue-primary hover:cursor-pointer hover:shadow-lg peer-checked:bg-venue-primary peer-checked:text-venue-secondary peer-checked:shadow-venue-brand-style"
        }
      >
        <div className="mx-auto flex max-w-screen-sm items-center justify-start space-x-32 sm:flex-col sm:justify-between sm:space-x-0 sm:space-y-12">
          <div className="w-1/3 sm:w-full">{image}</div>
          <div className="flex-1 text-left sm:space-y-12 sm:text-center">
            <div className="font-bold">{title}</div>
            <HelperText>{description}</HelperText>
          </div>
        </div>
      </label>
    </div>
  );
};
const VoucherCartItemForm = () => {
  const { t } = useTranslation("onsite", {
    keyPrefix: "voucherCart",
  });
  const [searchParams, _] = useSearchParams();
  const voucherCartItemUuid = useParams().voucherCartItemUuid;
  const venue = useVenue();
  const voucherConfig = useSuspenseQuery({
    ...VenueOnlineConfigQueries.current(),
  }).data;
  const voucherCartUuid = useSuspenseQuery(VoucherCartQueries.current).data
    .uuid;
  const voucherCartItem = useSuspenseQuery({
    ...VoucherCartItemsQueries.cartItems(voucherCartUuid),
    select: (data) => {
      return data.find((item) => item.uuid === voucherCartItemUuid);
    },
  }).data;

  const voucherTemplateUuid =
    searchParams.get("voucherTemplateUuid") ||
    voucherCartItem?.voucherTemplateUuid;
  const voucherTemplate = useSuspenseQuery({
    ...VoucherTemplatesQueries.all(),
    select: (data) => {
      return data.find((template) => template.uuid === voucherTemplateUuid);
    },
  }).data;

  const saveVoucherCartItemMutation = useSaveVoucherCartItem();
  const schema = getFormDataSchema({
    maxValue: voucherConfig.maxVoucherAmount,
    minValue: voucherConfig.minVoucherAmount,
  });

  const formProps = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: "onBlur",
    defaultValues: {
      creationReason: "gift_for_someone_else",
      voucherTemplateUuid: voucherTemplateUuid,
      ...voucherCartItem,
    },
  });

  const navigate = useNavigate();
  const onSubmit = async (data: FormData) => {
    await saveVoucherCartItemMutation.mutateAsync({
      voucherCartUuid,
      voucherTemplateUuid: voucherTemplateUuid || undefined,
      uuid: voucherCartItemUuid,
      ...data,
    });
    navigate("../checkout");
  };
  const deleteVoucherCartItemMutation =
    useDeleteVoucherCartItem(voucherCartUuid);

  const onDelete = () => {
    if (!voucherCartItem) {
      return;
    }
    deleteVoucherCartItemMutation.mutate(voucherCartItem.uuid);
    navigate("../checkout");
  };

  const watchedValue = formProps.watch("value");
  const voucherValueInCents =
    (watchedValue ? watchedValue * 100 : voucherTemplate?.valueInCents) || 0;
  if (!voucherConfig.customVoucherAmounts && !voucherTemplate) {
    throw new Error(
      "No voucher template found and custom amounts are disabled",
    );
  }
  return (
    <div>
      <NavBarTitle
        title={voucherCartItem ? t("editVoucher") : t("addVoucher")}
      />
      <form
        className="mx-auto space-y-32 md:max-w-screen-sm"
        onSubmit={formProps.handleSubmit(onSubmit, console.log)}
      >
        <Label>{t("whoIsItFor")}</Label>
        <div className="items-stretch justify-between space-y-12 sm:flex sm:space-x-12 sm:space-y-0">
          <VoucherRadioOption
            formProps={formProps}
            value="gift_for_someone_else"
            image={
              <img
                className="mx-auto h-128 drop-shadow-xl"
                src={GiftForSomeoneElse}
              />
            }
            title={t("forSomeoneElse_title")}
            description={t("forSomeoneElse_description")}
          />
          <VoucherRadioOption
            formProps={formProps}
            value="gift_for_unknown_recipient"
            image={
              <img
                className="mx-auto h-128 drop-shadow-xl"
                src={GiftForUnknownRecipient}
              />
            }
            title={t("unknownRecipient_title")}
            description={t("unknownRecipient_description")}
          />
          <VoucherRadioOption
            formProps={formProps}
            value="self_purchase"
            image={
              <img
                className="mx-auto h-128 drop-shadow-xl"
                src={SelfPurchase}
              />
            }
            title={t("forMe_title")}
            description={t("forMe_description")}
          />
        </div>
        <input
          type="hidden"
          {...(formProps.register("voucherTemplateUuid") || "none")}
        />
        <AnimatePresence initial={false}>
          {formProps.watch("creationReason") === "gift_for_someone_else" && (
            <motion.div
              variants={collapseY}
              layout
              key="someone-else"
              initial={"initial"}
              animate={"animate"}
              exit={"exit"}
              className="space-y-32"
            >
              <TextField
                name="recipientName"
                label={t("recipientName")}
                placeholder={t("recipientName_placeholder")}
                helpText={t("recipientName_helpText")}
                formProps={formProps}
              />
            </motion.div>
          )}

          {formProps.watch("creationReason") != "self_purchase" && (
            <motion.div
              className="space-y-32 overflow-hidden"
              variants={collapseY}
              layout
              key="message"
              initial={"initial"}
              animate={"animate"}
              exit={"exit"}
            >
              <TextAreaField
                name="message"
                label={t("message")}
                placeholder={t("message_placeholder")}
                helpText={t("message_helpText")}
                formProps={formProps}
              />
              <TextField
                name="senderName"
                label={t("senderName")}
                formProps={formProps}
                placeholder={t("senderName_placeholder")}
                helpText={t("senderName_helpText")}
              />
            </motion.div>
          )}
        </AnimatePresence>
        {!voucherTemplate && (
          <div>
            <FormattedCurrencyField
              name="value"
              label={t("amount")}
              formProps={formProps}
              decimalScale={0}
              helpText={
                <Trans
                  i18nKey={"validAmountBetween"}
                  t={t}
                  components={{
                    Min: (
                      <Price
                        showCurrency
                        value={voucherConfig.minVoucherAmount}
                      />
                    ),
                    Max: (
                      <Price
                        showCurrency
                        value={voucherConfig.maxVoucherAmount}
                      />
                    ),
                  }}
                />
              }
            />
          </div>
        )}
        <div className="space-y-16">
          <h3 className="block space-y-8 text-xl font-bold">{t("preview")}</h3>
          <VoucherCardPreview
            voucher={{
              recipientName: formProps.watch("recipientName") || "",
              giftMessage: formProps.watch("message") || "",
              senderName: formProps.watch("senderName") || "",
              venueName: venue.name || "",
              voucherName: voucherTemplate?.name || "",
              openingBalanceInCents: voucherValueInCents,
              balanceInCents: voucherValueInCents,
              expiresAt: DateTime.now().plus({ years: 3 }).toISO() || "",
              qrCode: t("unpaidQRCode"),
              code: t("unpaidCode"),
            }}
          />
          {formProps.watch("creationReason") === "gift_for_someone_else" && (
            <motion.div
              variants={collapseY}
              layout
              key="someone-else"
              initial={"initial"}
              animate={"animate"}
              exit={"exit"}
              className="space-y-32"
            >
              <TextField
                name="recipientEmail"
                label={t("recipientEmail")}
                helpText={t("recipientEmail_helpText")}
                placeholder={t("recipientEmail_placeholder")}
                formProps={formProps}
              />
            </motion.div>
          )}

          <div className="hidden flex-1 md:flex md:space-x-12">
            {voucherCartItem && (
              <div className="flex-none">
                <Button type="button" onClick={onDelete}>
                  {t("delete")}
                </Button>
              </div>
            )}
            <div className="flex-1">
              <Button
                type="submit"
                primary
                onClick={formProps.handleSubmit(onSubmit)}
              >
                {t("submit")}
              </Button>
            </div>
          </div>
        </div>
        <div className="sticky inset-x-0 bottom-0 flex">
          <PrimaryAction>
            <div className="flex items-center space-x-8">
              {voucherCartItem && (
                <div>
                  <Button onClick={onDelete} type="button">
                    <MdDelete className="text-xl" />
                  </Button>
                </div>
              )}
              <div className="flex-1">
                <Button
                  type="submit"
                  primary
                  onClick={formProps.handleSubmit(onSubmit)}
                >
                  {t("submit")}
                </Button>
              </div>
            </div>
          </PrimaryAction>
        </div>
      </form>
    </div>
  );
};

export default VoucherCartItemForm;
